import {
  Button,
  Collapse,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Paper,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import {AddCircle, Delete, ExpandLess, ExpandMore} from '@material-ui/icons';
import {
  changeBeneficiaryDetails,
  fetchBeneficiaryDetails,
  updateBeneficiaryDetails,
  validateAndSaveBeneficiaryPolicy,
} from 'Beneficiaries/ducks/beneficiaryDetail';
import {
  BeneficiaryPerson,
  EmptyPersonType,
  ValidBeneficiaryPerson,
  ValidPersonType,
} from 'Beneficiaries/models/BeneficiaryPerson';
import {ContingentBeneficiary, PrimaryBeneficiary} from 'Beneficiaries/models/PersonTypes';
import {BeneficiariesRouteConstants} from 'Beneficiaries/Routes';
import {theme} from 'Core/components/WithTheme';
import {Empty, isLoaded, Loaded} from 'Core/models/Async';
import {Eq} from 'fp-ts/lib/Eq';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {toFormattedDate} from 'Shared/utilities/DateFormatter';
import './Beneficiaries.sass';
import './WalmartBeneficiaryDetail.sass';
import WalmartBeneficiaryForm from './WalmartBeneficiaryForm';

const WalmartBeneficiaryDetail = () => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const history = useHistory();
  const {policyNumber} = useParams();
  const {beneficiaryDetails, originalBeneficiaryDetails} = useSelector(
    (state: ReducerState) => state.beneficiaryDetail
  );

  useEffect(() => {
    if (!policyNumber) {
      history.push(BeneficiariesRouteConstants.BENEFICIARIES);
    } else {
      dispatch(fetchBeneficiaryDetails(policyNumber));
    }

    return () => {
      dispatch(changeBeneficiaryDetails(new Empty()));
    };
  }, [policyNumber]);

  const [open, setOpen] = useState<boolean[]>([]);
  const handleClick = (index: number) => {
    setOpen(open.map((value, i) => (index === i ? !value : value)));
  };

  useEffect(() => {
    if (isLoaded(beneficiaryDetails) && beneficiaryDetails.Data && open.length !== beneficiaryDetails.Data.length) {
      // Initilize the array
      setOpen(beneficiaryDetails.Data.map(() => false));
    }
  }, [beneficiaryDetails]);

  // If person is of type beneficiary, then allow add or delete, as well as all editing
  const isBeneficiary = (personTypeId?: number) =>
    personTypeId === PrimaryBeneficiary.id || personTypeId === ContingentBeneficiary.id;

  const updateField = (index: number) => (field: {[key in keyof BeneficiaryPerson]: BeneficiaryPerson[key]}) =>
    dispatch(
      updateBeneficiaryDetails(
        new Loaded(
          (beneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data.map((value, i) =>
            i === index ? {...value, ...field} : value
          )
        )
      )
    );

  const validatePercentage = (personType?: ValidPersonType | EmptyPersonType): boolean =>
    !isBeneficiary(personType?.id) ||
    ((beneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data.filter(
      p => !p.markedAsDeleted && p.personType?.id === personType?.id
    )
      .map(p => p.beneficiaryPercent || 0)
      .reduce((a, b) => a! + b!, 0) || 0) === 100;

  const equalBeneficiaryPerson: Eq<BeneficiaryPerson> = {
    equals: (x, y) =>
      x.identifier?.value === y.identifier?.value &&
      x.firstName === y.firstName &&
      x.middleName === y.middleName &&
      x.lastName === y.lastName &&
      x.gender === y.gender &&
      x.phoneNumber?.phoneNumber === y.phoneNumber?.phoneNumber &&
      x.personType === y.personType &&
      x.dateOfBirth?.value === y.dateOfBirth?.value &&
      x.email?.address === y.email?.address &&
      x.relationship === y.relationship &&
      x.address1 === y.address1 &&
      x.address2 === y.address2 &&
      x.address3 === y.address3 &&
      x.city === y.city &&
      x.state === y.state &&
      x.zipcode === y.zipcode &&
      x.beneficiaryPercent === y.beneficiaryPercent &&
      x.signatureDate === y.signatureDate &&
      x.sourceSystemIdentifier?.value === y.sourceSystemIdentifier?.value &&
      x.markedAsDeleted === y.markedAsDeleted,
  };

  const isEntirePolicyValidAndHasChanges = () => {
    const persons = (beneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data;
    const allPersonsAreValid = persons?.every(
      p => p.markedAsDeleted || ValidBeneficiaryPerson.create(p, true) instanceof ValidBeneficiaryPerson
    );
    const primaryBeneficiariesPercentageIsValid =
      persons.filter(p => p.personType?.id === PrimaryBeneficiary.id && !p.markedAsDeleted).length === 0 ||
      validatePercentage(PrimaryBeneficiary);
    const contingentBeneficiariesPercentageIsValid =
      persons.filter(p => p.personType?.id === ContingentBeneficiary.id && !p.markedAsDeleted).length === 0 ||
      validatePercentage(ContingentBeneficiary);
    const hasChanges = persons.some(
      (p, index) =>
        (originalBeneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data.length <= index ||
        !equalBeneficiaryPerson.equals(p, (originalBeneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data[index])
    );

    return (
      allPersonsAreValid &&
      primaryBeneficiariesPercentageIsValid &&
      contingentBeneficiariesPercentageIsValid &&
      hasChanges
    );
  };

  const renderLabelValuePairs = (pairs: Array<{label: string; value?: string}>, markedAsDeleted?: boolean) => (
    <>
      {isMobile ? (
        <Grid item container spacing={2} direction="column">
          {pairs.map(p => (
            <Grid item key={`${p.label}_${p.value}`}>
              <Typography variant="caption" color="textSecondary">
                {p.label}
              </Typography>
              <Typography
                className={markedAsDeleted ? 'walmart-beneficiary-detail__strike-through' : ''}
                variant="subtitle2"
                noWrap
              >
                {p.value}
              </Typography>
            </Grid>
          ))}
        </Grid>
      ) : (
        <>
          <Grid item container spacing={2}>
            {pairs.map(p => (
              <Grid item lg={2} key={`${p.label}_${p.value}`}>
                <Typography variant="caption" color="textSecondary">
                  {p.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
          <Grid item container spacing={2}>
            {pairs.map(p => (
              <Grid item lg={2} key={`${p.label}_${p.value}`}>
                <Typography
                  className={markedAsDeleted ? 'walmart-beneficiary-detail__strike-through' : ''}
                  variant="subtitle2"
                  noWrap
                >
                  {p.value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </>
  );

  return renderAsyncView(beneficiaryDetails, result => (
    <div className="walmart-beneficiary-detail">
      {result.map((person, index) => {
        const originalPerson: BeneficiaryPerson = (originalBeneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data[
          index
        ];

        return (
          <Paper
            className="walmart-beneficiary-detail__paper"
            key={`${person.id || `Walmart-Beneficiary-Detail_${index}`}`}
          >
            {originalPerson ? (
              <List>
                <ListItem button onClick={() => handleClick(index)}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item container lg={11}>
                      {renderLabelValuePairs(
                        [
                          {label: 'Name', value: `${originalPerson.firstName || ''} ${originalPerson.lastName || ''}`},
                          {label: 'Person Type', value: originalPerson.personType?.name},
                          {label: 'Date of Birth', value: toFormattedDate(originalPerson.dateOfBirth?.value)},
                          {
                            label: 'Percentage',
                            value:
                              originalPerson.personType?.id === PrimaryBeneficiary.id ||
                              originalPerson.personType?.id === ContingentBeneficiary.id
                                ? `${originalPerson.beneficiaryPercent || 0}%`
                                : 'N/A',
                          },
                          {label: "Signature Date", value: toFormattedDate(`${originalPerson.signatureDate || '01-01-1900'}`)},
                        ],
                        person.markedAsDeleted
                      )}
                    </Grid>
                    <Grid item container lg={2} justify="center" alignItems="center">
                      {open[index] ? <ExpandLess /> : <ExpandMore />}
                    </Grid>
                  </Grid>
                  <ListItemSecondaryAction>
                    {(isBeneficiary(originalPerson.personType?.id) || !person.id) &&
                      (!person.markedAsDeleted ? (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => updateField(index)({markedAsDeleted: true})}
                        >
                          <Delete color="error" />
                        </IconButton>
                      ) : (
                        <Button
                          aria-label="undo"
                          color="primary"
                          onClick={() => updateField(index)({markedAsDeleted: false})}
                        >
                          Undo
                        </Button>
                      ))}
                  </ListItemSecondaryAction>
                </ListItem>
                <Collapse in={open[index]} timeout="auto" unmountOnExit>
                  <Divider />
                  <WalmartBeneficiaryForm
                    beneficiaryPerson={person}
                    originalBeneficiaryPerson={originalPerson}
                    onFieldChange={updateField(index)}
                    percentageError={!validatePercentage(person.personType)}
                    index={index}
                  />
                </Collapse>
              </List>
            ) : (
              <WalmartBeneficiaryForm
                beneficiaryPerson={person}
                originalBeneficiaryPerson={originalPerson}
                onFieldChange={updateField(index)}
                percentageError={!validatePercentage(person.personType)}
                index={index}
              />
            )}
          </Paper>
        );
      })}
      <Button
        className="walmart-beneficiary-detail__add"
        color="primary"
        variant="outlined"
        fullWidth
        startIcon={<AddCircle color="primary" />}
        onClick={() =>
          dispatch(
            updateBeneficiaryDetails(
              new Loaded((beneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data.concat([{beneficiaryPercent: 0}]))
            )
          )
        }
      >
        Add New Beneficiary
      </Button>
      <Grid className="walmart-beneficiary-detail__actions" container justify="flex-end">
        <Button
          color="primary"
          variant="contained"
          onClick={() => history.push(BeneficiariesRouteConstants.BENEFICIARIES)}
        >
          Back
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!isEntirePolicyValidAndHasChanges()}
          onClick={() =>
            dispatch(
              validateAndSaveBeneficiaryPolicy(
                (beneficiaryDetails as Loaded<BeneficiaryPerson[]>).Data.filter(p => !p.markedAsDeleted),
                policyNumber!
              )
            )
          }
        >
          Save All Changes
        </Button>
      </Grid>
    </div>
  ));
};
export default WalmartBeneficiaryDetail;
